/**
 * QuantumFlowSync.AI - Admin Protected Route Component
 * Supports both OAuth users (with superadmin/admin role) and Employee auth
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Shield, AlertTriangle } from 'lucide-react';
import { useAuth as useEmployeeAuth } from '../contexts/AuthContext';
import { useAuth as useOAuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../App';

const AdminProtectedRoute = ({ 
  children, 
  requiredPermission = null,
  requiredTier = null,
  requireAny = null,
  requireAll = null
}) => {
  const { employee, loading: employeeLoading, hasPermission, hasTier } = useEmployeeAuth();
  const oauthContext = useOAuthContext();
  const location = useLocation();
  const [oauthUser, setOAuthUser] = useState(null);
  const [oauthLoading, setOAuthLoading] = useState(true);

  // Fetch OAuth user if not authenticated as employee
  useEffect(() => {
    const fetchOAuthUser = async () => {
      if (employee) {
        // Already authenticated as employee, skip OAuth check
        setOAuthLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setOAuthUser(response.data);
      } catch (error) {
        console.error('OAuth user fetch failed:', error);
        setOAuthUser(null);
      } finally {
        setOAuthLoading(false);
      }
    };

    fetchOAuthUser();
  }, [employee]);

  // Show loading spinner while checking auth
  if (employeeLoading || oauthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated via OAuth with admin role
  const isOAuthAdmin = oauthUser && (oauthUser.role === 'superadmin' || oauthUser.role === 'admin');

  // Redirect to login if not authenticated via either method
  if (!employee && !isOAuthAdmin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // If OAuth admin, allow access without permission checks
  if (isOAuthAdmin) {
    return <>{children}</>;
  }

  // Employee auth - continue with existing permission checks
  if (employee) {
    // Check if employee is active
    if (employee.status !== 'active') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-yellow-500" />
              </div>
              <CardTitle className="text-2xl text-center">Account Suspended</CardTitle>
              <CardDescription className="text-center">
                Your account has been suspended. Please contact your administrator.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full" 
                onClick={() => window.location.href = '/admin/login'}
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Check tier requirement
    if (requiredTier !== null && !hasTier(requiredTier)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <Shield className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-center">Access Denied</CardTitle>
              <CardDescription className="text-center">
                You don't have sufficient permissions to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Required:</strong> Tier {requiredTier} or higher
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Your tier:</strong> {employee.roles?.[0]?.tier || 'Unknown'}
                </p>
              </div>
              <Button 
                className="w-full" 
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Check single permission requirement
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <Shield className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-center">Access Denied</CardTitle>
              <CardDescription className="text-center">
                You don't have permission to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Required permission:</strong>
                </p>
                <p className="text-sm font-mono mt-1">{requiredPermission}</p>
              </div>
              <Button 
                className="w-full" 
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Check requireAny (OR logic)
    if (requireAny && !requireAny.some(perm => hasPermission(perm))) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <Shield className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-center">Access Denied</CardTitle>
              <CardDescription className="text-center">
                You need at least one of the required permissions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Required (any):</strong>
                </p>
                <ul className="text-sm font-mono space-y-1">
                  {requireAny.map(perm => (
                    <li key={perm}>• {perm}</li>
                  ))}
                </ul>
              </div>
              <Button 
                className="w-full" 
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Check requireAll (AND logic)
    if (requireAll && !requireAll.every(perm => hasPermission(perm))) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <Shield className="h-12 w-12 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-center">Access Denied</CardTitle>
              <CardDescription className="text-center">
                You need all of the required permissions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Required (all):</strong>
                </p>
                <ul className="text-sm font-mono space-y-1">
                  {requireAll.map(perm => (
                    <li key={perm} className={hasPermission(perm) ? 'text-green-600' : 'text-red-600'}>
                      {hasPermission(perm) ? '✓' : '✗'} {perm}
                    </li>
                  ))}
                </ul>
              </div>
              <Button 
                className="w-full" 
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default AdminProtectedRoute;
